import {Trans} from '@lingui/macro';
import React, {ReactNode} from 'react';

import {UnreachableError} from '../../../../../libs/shared/helpers/src/types/customErrors';
import {buildTeammateDisplayName} from '../../../../helpers/modelHelpers';
import {InboxModel} from '../../../../models/api/inboxModel';
import {LensModel} from '../../../../models/api/lensModel';
import {TagModel} from '../../../../models/api/tagModel';
import {TeamModel} from '../../../../models/api/teamModel';
import {TeammatesFoldersEnum, TeamsFoldersEnum} from '../../../../models/url/inboxesUrlModel';
import {ResourceMap} from '../../../appTypes';
import {FindTeammate} from '../../../data/teammates/lazyTeammatesSelectors';
import {
  InboxesParameters,
  InboxesParametersTypesEnum,
  InboxesTeammateMessagesParameters,
  InboxesTeammateParameters,
  InboxesTeamParameters,
  InboxesUserParameters,
} from './inboxesParameters';
import {messageFolderToConversationFolder} from './inboxesParametersHelpers';

interface BuildParametersSearchLocalFilterNameOptions {
  shouldWrapAllNamesInQuotes: boolean;
  shouldWrapProperNounsInQuotes: boolean;
}

export function buildParametersSearchLocalFilterName(
  inboxes: ResourceMap<InboxModel>,
  tags: ResourceMap<TagModel>,
  lenses: ResourceMap<LensModel>,
  findTeammate: FindTeammate,
  teams: ResourceMap<TeamModel>,
  parameters: InboxesParameters,
  options: BuildParametersSearchLocalFilterNameOptions = {
    shouldWrapAllNamesInQuotes: true,
    shouldWrapProperNounsInQuotes: true,
  },
) {
  // Message cells.
  if (
    parameters.type === InboxesParametersTypesEnum.TEAMMATE_MESSAGES &&
    messageFolderToConversationFolder(parameters.folder) === TeammatesFoldersEnum.INBOX
  ) {
    return <Trans id="workspace-inbox">Inbox</Trans>;
  }

  // Conversation cells.
  return buildFormattedConversationParametersSearchLocalFilterName(
    inboxes,
    tags,
    lenses,
    findTeammate,
    teams,
    parameters,
    options,
  );
}

function buildFormattedConversationParametersSearchLocalFilterName(
  inboxes: ResourceMap<InboxModel>,
  tags: ResourceMap<TagModel>,
  lenses: ResourceMap<LensModel>,
  findTeammate: FindTeammate,
  teams: ResourceMap<TeamModel>,
  parameters: InboxesParameters,
  options: BuildParametersSearchLocalFilterNameOptions,
) {
  const renderedName = buildConversationParametersSearchLocalFilterName(
    inboxes,
    tags,
    lenses,
    findTeammate,
    teams,
    parameters,
    options,
  );
  return handleLocalFilterNameOptions(renderedName, options);
}

function buildConversationParametersSearchLocalFilterName(
  inboxes: ResourceMap<InboxModel>,
  tags: ResourceMap<TagModel>,
  lenses: ResourceMap<LensModel>,
  findTeammate: FindTeammate,
  teams: ResourceMap<TeamModel>,
  parameters: InboxesParameters,
  options: BuildParametersSearchLocalFilterNameOptions,
) {
  switch (parameters.type) {
    case InboxesParametersTypesEnum.INBOX:
    case InboxesParametersTypesEnum.TEAMMATE_INBOX: {
      const inbox = inboxes[parameters.inboxId];

      return inbox ? inbox.name : <Trans id="workspace-inbox">Inbox</Trans>;
    }

    case InboxesParametersTypesEnum.VIEW:
    case InboxesParametersTypesEnum.TEAMMATE_VIEW: {
      const tag = tags[parameters.viewId];

      return tag ? tag.name : <Trans id="workspace-tag">Tag</Trans>;
    }

    case InboxesParametersTypesEnum.TEAM_LENS:
    case InboxesParametersTypesEnum.TEAMMATE_LENS: {
      const lens = lenses[parameters.lensId];

      return lens ? lens.name : <Trans id="workspace-lens">View</Trans>;
    }

    case InboxesParametersTypesEnum.USER:
    case InboxesParametersTypesEnum.TEAMMATE:
    case InboxesParametersTypesEnum.TEAMMATE_MESSAGES:
      return buildTeammateParametersSearchLocalFilterName(findTeammate, parameters, options);

    case InboxesParametersTypesEnum.TEAM:
      return buildTeamParametersSearchLocalFilterName(teams, parameters, options);

    case InboxesParametersTypesEnum.GLOBAL:
      return <Trans id="workspace-assigned">All teammates</Trans>;

    default:
      throw new UnreachableError(parameters);
  }
}

function handleLocalFilterNameOptions(
  name: ReactNode,
  options: BuildParametersSearchLocalFilterNameOptions,
  isProperNoun = false,
) {
  const {shouldWrapAllNamesInQuotes, shouldWrapProperNounsInQuotes} = options;

  if (!isProperNoun) {
    return shouldWrapAllNamesInQuotes ? <>&quot;{name}&quot;</> : name;
  }

  if (!shouldWrapAllNamesInQuotes && shouldWrapProperNounsInQuotes) {
    // For proper nouns that should be wrapped, only wrap in quotes if we're not wrapping all names.
    return <>&quot;{name}&quot;</>;
  }

  return name;
}

function buildTeammateParametersSearchLocalFilterName(
  findTeammate: FindTeammate,
  parameters: InboxesUserParameters | InboxesTeammateParameters | InboxesTeammateMessagesParameters,
  options: BuildParametersSearchLocalFilterNameOptions,
) {
  switch (parameters.folder) {
    case TeammatesFoldersEnum.ASSIGNED:
      return <Trans id="workspace-assignedtome">Assigned to me</Trans>;

    case TeammatesFoldersEnum.ASSIGNED_SHARED: {
      const teammate = findTeammate(parameters.teammateId, 'InboxesParametersSearchFiltersHelpers');
      if (!teammate) {
        return undefined;
      }

      const teammateName = buildTeammateDisplayName(teammate);
      return handleLocalFilterNameOptions(teammateName, options, true);
    }

    case TeammatesFoldersEnum.SHARED_WITH_ME:
      return <Trans id="workspace-sharedwithme">Shared with me</Trans>;

    case TeammatesFoldersEnum.DISCUSSIONS:
      return <Trans id="workspace-discussions">Discussions</Trans>;

    case TeammatesFoldersEnum.TRASHED:
      return <Trans id="workspace-trash">Trash</Trans>;

    case TeammatesFoldersEnum.SPAM:
      return <Trans id="workspace-spam">Spam</Trans>;

    case TeammatesFoldersEnum.SENT:
      return <Trans id="workspace-sent">Sent</Trans>;

    default:
      return <Trans id="workspace-inbox">Inbox</Trans>;
  }
}

function buildTeamParametersSearchLocalFilterName(
  teams: ResourceMap<TeamModel>,
  parameters: InboxesTeamParameters,
  options: BuildParametersSearchLocalFilterNameOptions,
) {
  const team = teams[parameters.teamId];
  const teamName = team && handleLocalFilterNameOptions(team.name, options, true);

  switch (parameters.folder) {
    case TeamsFoldersEnum.TRASHED:
      return <Trans id="workspace-team-trash">{teamName} trash</Trans>;

    case TeamsFoldersEnum.SPAM:
      return <Trans id="workspace-team-spam">{teamName} spam</Trans>;

    default:
      return teamName;
  }
}
